import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';

import { Box, Divider, Link, Typography } from '@mui/material';
import {
  BrandLogo,
  Loader,
  PageWrapper,
  PositionSubscriptionForm,
  TextButton,
} from 'src/components';
import { SubscriptionCreated } from './components/SubscriptionCreated';
import { UnsubscribeReasonsForm } from './components/UnsubscribeReasonsForm';

import {
  createCandidatePositionSubscription,
  getSubscriptionDetails,
  positionSubscriptionSelectors,
  resubscribeReferralPositionSubscription,
  unsubscribePositionSubscription,
} from 'src/redux/positionSubscription';

import { isStatusComplete, isStatusIdle, isStatusLoading } from 'src/utils';
import { PositionSubscriptionType } from 'src/constants';
import { PositionSubscriptionFields } from 'src/types';

import { createCandidatePositionSubscriptionValidationSchema } from '../PositionSubscriptionCandidate/validationsSchema';
import { createReferralPositionSubscriptionValidationSchema } from '../PositionSubscriptionReferral/validationsSchema';

export const PositionSubscriptionUnsubscribed: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const { apiStatus: subscriptionDetailsAPIStatus, data: subscriptionDetails } =
    useAppSelector(positionSubscriptionSelectors.getSubscriptionDetailsAPIData);
  const unsubscribePositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getUnsubscribeSubscriptionAPIStatus,
  );
  const createPositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getCreateSubscriptionAPIStatus,
  );
  const resubscribePositionSubscriptionAPIStatus = useAppSelector(
    positionSubscriptionSelectors.getResubscribeSubscriptionAPIStatus,
  );

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      dispatch(getSubscriptionDetails(token));
    }
  }, []);

  const initialValues: PositionSubscriptionFields = useMemo(
    () => ({
      email: subscriptionDetails?.email,
      name: subscriptionDetails?.name,
      locations: subscriptionDetails?.locations,
      technicalFlows: subscriptionDetails?.technicalFlows,
      experience: subscriptionDetails?.experience,
      frequency: subscriptionDetails?.frequency,
    }),
    [subscriptionDetails],
  );
  const disabledFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () => ['email'],
    [],
  );

  const requiredFields: Array<keyof PositionSubscriptionFields> = useMemo(
    () =>
      subscriptionDetails?.type === PositionSubscriptionType.Candidate
        ? ['email', 'name', 'locations', 'technicalFlows', 'experience']
        : subscriptionDetails?.type === PositionSubscriptionType.Referral
        ? ['email', 'frequency']
        : [],
    [subscriptionDetails],
  );

  const resubscribeAPIStatus =
    subscriptionDetails?.type === PositionSubscriptionType.Referral
      ? resubscribePositionSubscriptionAPIStatus
      : createPositionSubscriptionAPIStatus;

  const onPositionSubscriptionFormSubmit = useCallback(
    (values: PositionSubscriptionFields) => {
      if (subscriptionDetails?.type === PositionSubscriptionType.Candidate) {
        dispatch(
          createCandidatePositionSubscription({
            email: values.email!,
            name: values.name!,
            locations: values.locations!,
            technicalFlows: values.technicalFlows!,
            experience: values.experience!,
          }),
        );
      }
      if (subscriptionDetails?.type === PositionSubscriptionType.Referral) {
        dispatch(
          resubscribeReferralPositionSubscription({
            token: token!,
            locations: values.locations || null,
            technicalFlows: values.technicalFlows || null,
            frequency: values.frequency!,
          }),
        );
      }
    },
    [subscriptionDetails?.type, token],
  );

  const onSendFeedbackClick = useCallback(() => {
    window.open(
      'https://docs.google.com/forms/d/1HhsAo1_kmFdOG3h7ZjAXpp_Do7d6ZXinBoswXaGL0GM/viewform',
      '_blank',
      'noreferrer',
    );
  }, []);

  const onPositionSubscriptionUnsubscribe = useCallback(
    (reason: string) => {
      if (token) {
        dispatch(unsubscribePositionSubscription({ token, reason }));
      }
    },
    [token],
  );

  if (
    isStatusLoading(subscriptionDetailsAPIStatus) ||
    isStatusIdle(subscriptionDetailsAPIStatus)
  ) {
    return (
      <PageWrapper>
        <Loader showBrandLogo />
      </PageWrapper>
    );
  }

  if (!subscriptionDetails) {
    return (
      <PageWrapper>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
          data-testid="is-loading"
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              m: 6,
            }}
          >
            <BrandLogo />
          </Box>
          <Typography variant="h6" fontWeight="600" textAlign="center">
            Subscription not found
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        background: theme.palette.background.backgroundPrimary,
        minWidth: '100%',
        minHeight: '100%',
        padding: '41px 16px',
        [theme.breakpoints.down('lg')]: {
          paddingTop: '130px',
        },
      })}
    >
      <Box sx={{ position: 'absolute', top: '49px', left: '66px' }}>
        <BrandLogo width={133} />
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column">
        <Box
          sx={(theme) => ({
            background: theme.palette.common.white,
            borderRadius: '48px',
            width: '603px',
            position: 'relative',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '62px 100px',
              width: '100%',
            }}
          >
            {isStatusComplete(resubscribeAPIStatus) ? (
              <SubscriptionCreated
                email={subscriptionDetails.email}
                subscriptionType={subscriptionDetails.type}
              />
            ) : (
              <>
                <UnsubscribeReasonsForm
                  userName={subscriptionDetails.name}
                  type={subscriptionDetails.type}
                  isLoading={isStatusLoading(
                    unsubscribePositionSubscriptionAPIStatus,
                  )}
                  isSubmitted={isStatusComplete(
                    unsubscribePositionSubscriptionAPIStatus,
                  )}
                  onSubmit={onPositionSubscriptionUnsubscribe}
                />

                {isStatusComplete(unsubscribePositionSubscriptionAPIStatus) && (
                  <>
                    <Divider sx={{ width: '100%', mb: 1 }} />

                    <Typography mb={2} textAlign="left">
                      In case you’re not happy with the frequency or selection
                      of the positions, you can reconfigure your subscription:
                    </Typography>
                    <PositionSubscriptionForm
                      initialValues={initialValues}
                      disabledFields={disabledFields}
                      requiredFields={requiredFields}
                      validationSchema={
                        subscriptionDetails.type ===
                        PositionSubscriptionType.Candidate
                          ? createCandidatePositionSubscriptionValidationSchema
                          : createReferralPositionSubscriptionValidationSchema
                      }
                      type={subscriptionDetails.type}
                      submissionBtnLabel="Subscribe again"
                      apiStatus={resubscribeAPIStatus}
                      onSubmit={onPositionSubscriptionFormSubmit}
                    />
                  </>
                )}

                {subscriptionDetails.type ===
                  PositionSubscriptionType.Referral && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    mt={2}
                    width="100%"
                  >
                    <TextButton onClick={onSendFeedbackClick} variant="primary">
                      Send Feedback
                    </TextButton>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" mt={6}>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link component={RouterLink} to="/terms-of-use">
              Terms of use
            </Link>
          </Typography>
          <Typography
            variant="body2"
            color="secondary"
            textAlign="center"
            fontSize="0.675rem"
            mx={2}
          >
            <Link href="https://agileengine.com/privacy-policy" target="_blank">
              Privacy policy
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
